import React, { LiHTMLAttributes, useState } from 'react';

import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import OutsideClickHandler from 'react-outside-click-handler';

const Wrapper = styled.div`
  position: relative;
  z-index: 999;
`;

interface MenuContainerProps {
  $top?: number;
  $left?: number;
  $right?: number;
  $bottom?: number;
  $minWidth?: number;
}

const MenuContainer = styled.div<MenuContainerProps>`
  position: absolute;
  font-size: 14px;
  background-color: ${(props) => props.theme.global.bgColor};
  border-radius: 4px;
  box-shadow: 0 1px 4px 0 rgb(0 0 0 / 24%);
  min-width: ${(props) => (props.$minWidth ? `${props.$minWidth}px` : 'initial')};
  background-color: ${(props) => props.theme.ui.menu.bgColor};
  color: ${(props) => props.theme.global.textColor};

  ${(props) => ({
    ...(props.$top !== undefined && { top: `${props.$top}px` }),
    ...(props.$left !== undefined && { left: `${props.$left}px` }),
    ...(props.$right !== undefined && { right: `${props.$right}px` }),
    ...(props.$bottom !== undefined && { bottom: `${props.$bottom}px` }),
  })};
`;

const SMenuItem = styled.li<{ $padding: string; $disabled?: boolean; $hasHoverEffect?: boolean }>`
  position: relative;
  display: flex;
  white-space: nowrap;

  &:hover {
    ${(props) => {
      if (!props.$disabled && props.$hasHoverEffect) {
        return {
          backgroundColor: props.theme.global.hoverBgColor,
        };
      }
    }}
  }

  & > * {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 12px;
    padding: ${(props) => props.$padding};
    width: 100%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: initial;
  top: -6px;
  right: -10px;
  font-size: 18px;
  color: ${(props) => props.theme.global.lightTextColor};
`;

export const MenuItemIcon = styled.span<{ size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: ${(props) => `${props.size ?? 20}px`};
  }
`;

export const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 6px 0px;
  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.global.borderColor};
  }
`;

interface MenuItemProps extends LiHTMLAttributes<HTMLLIElement> {
  children: React.ReactNode;
  disabled?: boolean;
  padding?: string;
  hasHoverEffect?: boolean;
  onCloseClick?: () => void;
}

export const MenuItem = React.forwardRef(
  (
    { children, disabled, padding = '8px 18px', hasHoverEffect = true, onCloseClick, ...rest }: MenuItemProps,
    ref: React.ForwardedRef<HTMLLIElement>
  ) => {
    return (
      <SMenuItem
        ref={ref}
        {...(!disabled && { className: 'menu-close-trigger' })}
        $padding={padding}
        $disabled={disabled}
        $hasHoverEffect={hasHoverEffect}
        {...rest}
      >
        {children}
        {onCloseClick && (
          <CloseButton onClick={onCloseClick}>
            <IoClose />
          </CloseButton>
        )}
      </SMenuItem>
    );
  }
);

interface MenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  horizontal?: 'left' | 'right';
  vertical?: 'top' | 'bottom';
  minWidth?: number;
  onClose?: () => void;
  onTriggerClick?: () => void;
}

const Menu = ({
  children,
  trigger,
  horizontal = 'right',
  vertical = 'bottom',
  minWidth,
  onClose,
  onTriggerClick,
}: MenuProps) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleToggleShowMenu = () => {
    if (onTriggerClick) onTriggerClick();
    setShowMenu((prev) => {
      const newShowMenu = !prev;

      if (!newShowMenu && onClose) {
        onClose();
      }

      return newShowMenu;
    });
  };

  const handleMenuClick = (event: any) => {
    const menuListItem = event.target.closest('.menu-close-trigger');

    if (menuListItem) {
      setShowMenu(false);
    }
  };

  const handleOutSideClick = () => {
    if (onClose && showMenu) {
      onClose();
    }
    setShowMenu(false);
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOutSideClick}>
      <Wrapper className="no-dragging">
        {React.cloneElement(trigger as any, {
          style: { cursor: 'pointer' },
          onClick: handleToggleShowMenu,
        })}
        {showMenu && (
          <MenuContainer
            onClick={handleMenuClick}
            $top={vertical === 'top' ? 0 : undefined}
            $left={horizontal === 'left' ? 0 : undefined}
            $right={horizontal === 'right' ? 0 : undefined}
            $minWidth={minWidth}
          >
            {children}
          </MenuContainer>
        )}
      </Wrapper>
    </OutsideClickHandler>
  );
};

export default Menu;
